"use strict";
// Copyright (C) 2017-2026 Smart code 203358507
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var react_i18next_1 = require("react-i18next");
var common_1 = require("stremio/common");
var withFallbackLabels = function (tracks) {
    if (!Array.isArray(tracks)) {
        return [];
    }
    return tracks.map(function (track) { return (__assign(__assign({}, track), { label: track.label || track.url || '' })); });
};
var findTrackById = function (tracks, id) {
    if (!id) {
        return undefined;
    }
    return tracks.find(function (track) { return track.id === id; });
};
var findTrackByLanguage = function (tracks, language) {
    if (!language) {
        return undefined;
    }
    var languageCode = common_1.languages.toCode(language);
    return tracks.find(function (track) {
        return track.lang === language || common_1.languages.toCode(track.lang) === languageCode;
    });
};
var useSubtitles = function (_a) {
    var player = _a.player, video = _a.video, settings = _a.settings, streamStateChanged = _a.streamStateChanged, menusOpen = _a.menusOpen, closeMenus = _a.closeMenus, closeSubtitlesMenu = _a.closeSubtitlesMenu, toggleSubtitlesMenu = _a.toggleSubtitlesMenu;
    var t = (0, react_i18next_1.useTranslation)().t;
    var toast = (0, common_1.useToast)();
    var videoRef = (0, react_1.useRef)(video);
    var settingsRef = (0, react_1.useRef)(settings);
    var defaultTrackSelected = (0, react_1.useRef)(false);
    var lastSelectedTrack = (0, react_1.useRef)(null);
    videoRef.current = video;
    settingsRef.current = settings;
    var streamSubtitles = (0, react_1.useMemo)(function () {
        var _a;
        return withFallbackLabels((_a = player.selected) === null || _a === void 0 ? void 0 : _a.stream.subtitles);
    }, [player.selected]);
    var externalSubtitles = (0, react_1.useMemo)(function () {
        return withFallbackLabels(player.subtitles);
    }, [player.subtitles]);
    var allTracks = (0, react_1.useMemo)(function () {
        return video.state.subtitlesTracks.concat(video.state.extraSubtitlesTracks);
    }, [video.state.subtitlesTracks, video.state.extraSubtitlesTracks]);
    var hasTracks = allTracks.length > 0;
    var applySubtitleStyle = (0, react_1.useCallback)(function () {
        var currentSettings = settingsRef.current;
        var currentVideo = videoRef.current;
        currentVideo.setSubtitlesSize(currentSettings.subtitlesSize);
        currentVideo.setSubtitlesOffset(currentSettings.subtitlesOffset);
        currentVideo.setSubtitlesTextColor(currentSettings.subtitlesTextColor);
        currentVideo.setSubtitlesBackgroundColor(currentSettings.subtitlesBackgroundColor);
        currentVideo.setSubtitlesOutlineColor(currentSettings.subtitlesOutlineColor);
    }, []);
    var rememberTrack = (0, react_1.useCallback)(function (track, embedded) {
        lastSelectedTrack.current = { id: track.id, embedded: embedded };
        streamStateChanged({
            subtitleTrack: {
                id: track.id,
                embedded: embedded,
                lang: track.lang,
            },
        });
    }, [streamStateChanged]);
    var disableSubtitles = (0, react_1.useCallback)(function () {
        defaultTrackSelected.current = true;
        video.setSubtitlesTrack(null);
        video.setExtraSubtitlesTrack(null);
        streamStateChanged({ subtitleTrack: null });
    }, [streamStateChanged, video]);
    var selectEmbeddedTrack = (0, react_1.useCallback)(function (track) {
        if (!track) {
            disableSubtitles();
            return;
        }
        defaultTrackSelected.current = true;
        video.setSubtitlesTrack(track.id);
        rememberTrack(track, true);
    }, [disableSubtitles, rememberTrack, video]);
    var selectExtraTrack = (0, react_1.useCallback)(function (track) {
        if (!track) {
            disableSubtitles();
            return;
        }
        defaultTrackSelected.current = true;
        video.setExtraSubtitlesTrack(track.id);
        rememberTrack(track, false);
    }, [disableSubtitles, rememberTrack, video]);
    var changeDelay = (0, react_1.useCallback)(function (delay) {
        video.setSubtitlesDelay(delay);
        streamStateChanged({ subtitleDelay: delay });
    }, [streamStateChanged, video]);
    var increaseDelay = (0, react_1.useCallback)(function () {
        var _a;
        changeDelay(((_a = video.state.extraSubtitlesDelay) !== null && _a !== void 0 ? _a : 0) + 250);
    }, [changeDelay, video.state.extraSubtitlesDelay]);
    var decreaseDelay = (0, react_1.useCallback)(function () {
        var _a;
        changeDelay(((_a = video.state.extraSubtitlesDelay) !== null && _a !== void 0 ? _a : 0) - 250);
    }, [changeDelay, video.state.extraSubtitlesDelay]);
    var changeSize = (0, react_1.useCallback)(function (size) {
        video.setSubtitlesSize(size);
        streamStateChanged({ subtitleSize: size });
    }, [streamStateChanged, video]);
    var updateSize = (0, react_1.useCallback)(function (delta) {
        var _a;
        var sizes = common_1.CONSTANTS.SUBTITLES_SIZES;
        var sizeIndex = sizes.indexOf((_a = video.state.subtitlesSize) !== null && _a !== void 0 ? _a : -1);
        var nextIndex = Math.max(0, Math.min(sizes.length - 1, sizeIndex + delta));
        changeSize(sizes[nextIndex]);
    }, [changeSize, video.state.subtitlesSize]);
    var changeOffset = (0, react_1.useCallback)(function (offset) {
        video.setSubtitlesOffset(offset);
        streamStateChanged({ subtitleOffset: offset });
    }, [streamStateChanged, video]);
    (0, common_1.onFileDrop)(common_1.CONSTANTS.SUPPORTED_LOCAL_SUBTITLES, function (file, buffer) {
        videoRef.current.addLocalSubtitles(file.name, buffer);
    });
    (0, react_1.useEffect)(function () {
        if (video.state.stream !== null) {
            video.addExtraSubtitlesTracks(externalSubtitles);
        }
    }, [externalSubtitles, video.state.stream]);
    (0, react_1.useEffect)(function () {
        var _a;
        if (defaultTrackSelected.current) {
            return;
        }
        if (settings.subtitlesLanguage === null) {
            video.setSubtitlesTrack(null);
            video.setExtraSubtitlesTrack(null);
            defaultTrackSelected.current = true;
            return;
        }
        var savedTrack = (_a = player.streamState) === null || _a === void 0 ? void 0 : _a.subtitleTrack;
        var savedTrackId = savedTrack === null || savedTrack === void 0 ? void 0 : savedTrack.id;
        var savedLanguage = savedTrack === null || savedTrack === void 0 ? void 0 : savedTrack.lang;
        var savedExternalTrack = Boolean(savedTrackId && (savedTrack === null || savedTrack === void 0 ? void 0 : savedTrack.embedded) === false);
        var embeddedTrack = savedTrackId ?
            findTrackById(video.state.subtitlesTracks, savedTrackId)
            :
                findTrackByLanguage(video.state.subtitlesTracks, savedLanguage !== null && savedLanguage !== void 0 ? savedLanguage : settings.subtitlesLanguage);
        var extraTrack = savedTrackId ?
            findTrackById(video.state.extraSubtitlesTracks, savedTrackId)
            :
                findTrackByLanguage(video.state.extraSubtitlesTracks, savedLanguage !== null && savedLanguage !== void 0 ? savedLanguage : settings.subtitlesLanguage);
        if (embeddedTrack === null || embeddedTrack === void 0 ? void 0 : embeddedTrack.id) {
            if (video.state.selectedSubtitlesTrackId !== embeddedTrack.id ||
                video.state.selectedExtraSubtitlesTrackId !== null) {
                video.setSubtitlesTrack(embeddedTrack.id);
            }
            defaultTrackSelected.current = true;
            return;
        }
        if (extraTrack === null || extraTrack === void 0 ? void 0 : extraTrack.id) {
            if (video.state.selectedExtraSubtitlesTrackId !== extraTrack.id ||
                video.state.selectedSubtitlesTrackId !== null) {
                video.setExtraSubtitlesTrack(extraTrack.id);
            }
            if (savedExternalTrack) {
                defaultTrackSelected.current = true;
            }
        }
    }, [
        player.streamState,
        settings.subtitlesLanguage,
        video.state.extraSubtitlesTracks,
        video.state.selectedExtraSubtitlesTrackId,
        video.state.selectedSubtitlesTrackId,
        video.state.subtitlesTracks,
    ]);
    (0, react_1.useEffect)(function () {
        var _a, _b, _c;
        if (video.state.stream === null) {
            return;
        }
        var delay = (_a = player.streamState) === null || _a === void 0 ? void 0 : _a.subtitleDelay;
        if (typeof delay === 'number') {
            video.setSubtitlesDelay(delay);
        }
        var size = (_b = player.streamState) === null || _b === void 0 ? void 0 : _b.subtitleSize;
        if (typeof size === 'number') {
            video.setSubtitlesSize(size);
        }
        var offset = (_c = player.streamState) === null || _c === void 0 ? void 0 : _c.subtitleOffset;
        if (typeof offset === 'number') {
            video.setSubtitlesOffset(offset);
        }
    }, [player.streamState, video.state.stream]);
    (0, react_1.useEffect)(function () {
        defaultTrackSelected.current = false;
        lastSelectedTrack.current = null;
    }, [video.state.stream]);
    (0, react_1.useEffect)(function () {
        if (!hasTracks) {
            closeSubtitlesMenu();
        }
    }, [closeSubtitlesMenu, hasTracks]);
    (0, react_1.useEffect)(function () {
        var onSubtitlesTrackLoaded = function () {
            toast.show({
                type: 'success',
                title: t('PLAYER_SUBTITLES_LOADED'),
                message: t('PLAYER_SUBTITLES_LOADED_EMBEDDED'),
                timeout: 3000,
            });
        };
        var onExtraSubtitlesTrackLoaded = function (track) {
            toast.show({
                type: 'success',
                title: t('PLAYER_SUBTITLES_LOADED'),
                message: track.exclusive ?
                    t('PLAYER_SUBTITLES_LOADED_EXCLUSIVE')
                    :
                        track.local ?
                            t('PLAYER_SUBTITLES_LOADED_LOCAL')
                            :
                                t('PLAYER_SUBTITLES_LOADED_ORIGIN', { origin: track.origin }),
                timeout: 3000,
            });
        };
        var onExtraSubtitlesTrackAdded = function (track) {
            if (track.local) {
                videoRef.current.setExtraSubtitlesTrack(track.id);
            }
        };
        video.events.on('subtitlesTrackLoaded', onSubtitlesTrackLoaded);
        video.events.on('extraSubtitlesTrackLoaded', onExtraSubtitlesTrackLoaded);
        video.events.on('extraSubtitlesTrackAdded', onExtraSubtitlesTrackAdded);
        video.events.on('implementationChanged', applySubtitleStyle);
        return function () {
            video.events.off('subtitlesTrackLoaded', onSubtitlesTrackLoaded);
            video.events.off('extraSubtitlesTrackLoaded', onExtraSubtitlesTrackLoaded);
            video.events.off('extraSubtitlesTrackAdded', onExtraSubtitlesTrackAdded);
            video.events.off('implementationChanged', applySubtitleStyle);
        };
    }, [applySubtitleStyle, t, toast, video.events]);
    (0, common_1.onShortcut)('subtitlesDelay', function (combo) {
        combo === 1 ? increaseDelay() : decreaseDelay();
    }, [increaseDelay, decreaseDelay], !menusOpen);
    (0, common_1.onShortcut)('subtitlesSize', function (combo) {
        combo === 1 ? updateSize(1) : updateSize(-1);
    }, [updateSize], !menusOpen);
    (0, common_1.onShortcut)('toggleSubtitles', function () {
        var _a, _b;
        var subtitlesEnabled = video.state.selectedSubtitlesTrackId !== null ||
            video.state.selectedExtraSubtitlesTrackId !== null;
        if (subtitlesEnabled) {
            if (video.state.selectedSubtitlesTrackId) {
                lastSelectedTrack.current = {
                    id: video.state.selectedSubtitlesTrackId,
                    embedded: true,
                };
            }
            else if (video.state.selectedExtraSubtitlesTrackId) {
                lastSelectedTrack.current = {
                    id: video.state.selectedExtraSubtitlesTrackId,
                    embedded: false,
                };
            }
            video.setSubtitlesTrack(null);
            video.setExtraSubtitlesTrack(null);
            return;
        }
        var savedTrack = (_b = (_a = player.streamState) === null || _a === void 0 ? void 0 : _a.subtitleTrack) !== null && _b !== void 0 ? _b : lastSelectedTrack.current;
        if (savedTrack === null || savedTrack === void 0 ? void 0 : savedTrack.id) {
            savedTrack.embedded ?
                video.setSubtitlesTrack(savedTrack.id)
                :
                    video.setExtraSubtitlesTrack(savedTrack.id);
        }
    }, [
        player.streamState,
        video.state.selectedExtraSubtitlesTrackId,
        video.state.selectedSubtitlesTrackId,
    ], !menusOpen);
    (0, common_1.onShortcut)('subtitlesMenu', function () {
        closeMenus();
        if (hasTracks) {
            toggleSubtitlesMenu();
        }
    }, [closeMenus, hasTracks, toggleSubtitlesMenu]);
    var menuProps = (0, react_1.useMemo)(function () { return ({
        subtitlesLanguage: settings.subtitlesLanguage,
        interfaceLanguage: settings.interfaceLanguage,
        subtitlesTracks: video.state.subtitlesTracks,
        selectedSubtitlesTrackId: video.state.selectedSubtitlesTrackId,
        subtitlesOffset: video.state.subtitlesOffset,
        subtitlesSize: video.state.subtitlesSize,
        extraSubtitlesTracks: video.state.extraSubtitlesTracks,
        selectedExtraSubtitlesTrackId: video.state.selectedExtraSubtitlesTrackId,
        extraSubtitlesOffset: video.state.extraSubtitlesOffset,
        extraSubtitlesDelay: video.state.extraSubtitlesDelay,
        extraSubtitlesSize: video.state.extraSubtitlesSize,
        onSubtitlesTrackSelected: selectEmbeddedTrack,
        onExtraSubtitlesTrackSelected: selectExtraTrack,
        onSubtitlesOffsetChanged: changeOffset,
        onSubtitlesSizeChanged: changeSize,
        onExtraSubtitlesOffsetChanged: changeOffset,
        onExtraSubtitlesDelayChanged: changeDelay,
        onExtraSubtitlesSizeChanged: changeSize,
    }); }, [
        changeDelay,
        changeOffset,
        changeSize,
        selectEmbeddedTrack,
        selectExtraTrack,
        settings.interfaceLanguage,
        settings.subtitlesLanguage,
        video.state.extraSubtitlesDelay,
        video.state.extraSubtitlesOffset,
        video.state.extraSubtitlesSize,
        video.state.extraSubtitlesTracks,
        video.state.selectedExtraSubtitlesTrackId,
        video.state.selectedSubtitlesTrackId,
        video.state.subtitlesOffset,
        video.state.subtitlesSize,
        video.state.subtitlesTracks,
    ]);
    return {
        streamSubtitles: streamSubtitles,
        allSubtitleTracks: allTracks,
        extraSubtitleTracks: video.state.extraSubtitlesTracks,
        selectedExtraSubtitleTrackId: video.state.selectedExtraSubtitlesTrackId,
        subtitlesMenuProps: menuProps,
    };
};
exports.default = useSubtitles;
